import React from "react";
import { Box, Grid2, Paper, useTheme } from "@mui/material";
import { getDailyTasksChipColors } from "./dailyTasksChips";
import {
  MockupWorkUrgency,
  RawWorkSummary,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";

export type RawWorkTileId = "all_pending" | "needs_inward" | "partial_inward" | "needs_qc";

interface RawWorkLedgerProps {
  summary: RawWorkSummary;
  workState: FilterValue[];
  workBucket: FilterValue[];
  onSelect: (tile: RawWorkTileId, urgency: MockupWorkUrgency) => void;
}

const TILES: Array<{
  id: RawWorkTileId;
  label: string;
  hint: string;
  countKey: keyof RawWorkSummary;
  todayKey: keyof RawWorkSummary;
  overdueKey: keyof RawWorkSummary;
  tone: "bad" | "ok" | "warn" | "info" | "neutral";
}> = [
  {
    id: "all_pending",
    label: "All",
    hint: "Everything still open",
    countKey: "all_pending",
    todayKey: "today",
    overdueKey: "overdue",
    tone: "neutral",
  },
  {
    id: "needs_inward",
    label: "Awaiting inward",
    hint: "No inward yet",
    countKey: "needs_inward",
    todayKey: "needs_inward_today",
    overdueKey: "needs_inward_overdue",
    tone: "bad",
  },
  {
    id: "partial_inward",
    label: "Partial inward",
    hint: "Some qty inwarded, not full — may already have rejects",
    countKey: "partial_inward",
    todayKey: "partial_inward_today",
    overdueKey: "partial_inward_overdue",
    tone: "info",
  },
  {
    id: "needs_qc",
    label: "Awaiting QC",
    hint: "QC not cleared — includes not started, in progress, on hold",
    countKey: "needs_qc",
    todayKey: "needs_qc_today",
    overdueKey: "needs_qc_overdue",
    tone: "warn",
  },
];

const RawWorkLedger: React.FC<RawWorkLedgerProps> = ({
  summary,
  workState,
  workBucket,
  onSelect,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const selectedStates = new Set(workState.map((item) => String(item.id)));
  const selectedUrgency = workBucket.map((item) => String(item.id))[0];
  const urgency: MockupWorkUrgency =
    selectedUrgency === "today" || selectedUrgency === "overdue"
      ? selectedUrgency
      : "any";
  const awaitingQcSelected = ["needs_qc", "qc_in_progress", "qc_on_hold"].some(
    (id) => selectedStates.has(id),
  );

  return (
    <Grid2 container spacing={1.5} columns={12} sx={{ mt: 2 }}>
      {TILES.map((tile) => {
        const nothingSelected = selectedStates.size === 0;
        const selected =
          tile.id === "all_pending"
            ? nothingSelected
            : tile.id === "needs_qc"
              ? awaitingQcSelected
              : selectedStates.has(tile.id);
        const tone = getDailyTasksChipColors(theme, tile.tone);
        const count =
          tile.countKey === "all_pending"
            ? (summary.all_pending ??
              summary.needs_inward +
                summary.partial_inward +
                summary.needs_qc)
            : (summary[tile.countKey] ?? 0);
        const todayCount = summary[tile.todayKey] ?? 0;
        const overdueCount = summary[tile.overdueKey] ?? 0;

        return (
          <Grid2 key={tile.id} size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              elevation={0}
              onClick={() => {
                try {
                  onSelect(tile.id, "any");
                } catch (error) {
                  console.error("Failed to select raw work tile:", error);
                }
              }}
              sx={{
                position: "relative",
                overflow: "hidden",
                p: 1.5,
                height: "100%",
                cursor: "pointer",
                borderRadius: 2,
                border: `1px solid ${selected ? tone.fg : colors.softCardBorder}`,
                bgcolor: selected ? tone.bg : colors.white,
                boxShadow: selected ? `inset 0 0 0 1px ${tone.fg}` : "none",
                transition: "border-color 120ms ease, background-color 120ms ease",
                "&:hover": {
                  borderColor: tone.fg,
                  bgcolor: selected ? tone.bg : `${tone.bg}`,
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: tone.fg,
                  opacity: selected ? 1 : 0.55,
                }}
              />
              <CustomTypography
                variant="caption"
                fontWeight={700}
                sx={{
                  color: selected ? tone.fg : colors.mutedSlateText,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  display: "block",
                }}
              >
                {tile.label}
              </CustomTypography>
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                sx={{
                  color: selected ? tone.fg : colors.navyText,
                  mt: 0.75,
                  display: "block",
                  lineHeight: 1,
                }}
              >
                {count}
              </CustomTypography>
              <CustomTypography
                variant="body3"
                fontWeight={500}
                sx={{ color: colors.greyTextColor, mt: 0.75, display: "block" }}
              >
                {tile.hint}
              </CustomTypography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                <UrgencyLink
                  label={`${todayCount} due today`}
                  active={selected && urgency === "today"}
                  color={colors.navyText}
                  activeColor={tone.fg}
                  onClick={() => onSelect(tile.id, "today")}
                />
                <UrgencyLink
                  label={
                    overdueCount > 0
                      ? `${overdueCount} past SLA`
                      : "On SLA"
                  }
                  active={selected && urgency === "overdue"}
                  color={
                    overdueCount > 0 ? colors.errorRed : colors.greyTextColor
                  }
                  activeColor={
                    overdueCount > 0 ? colors.errorRed : tone.fg
                  }
                  onClick={() => onSelect(tile.id, "overdue")}
                />
              </Box>
            </Paper>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

const UrgencyLink: React.FC<{
  label: string;
  active: boolean;
  color: string;
  activeColor: string;
  onClick: () => void;
}> = ({ label, active, color, activeColor, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        onClick();
      } catch (error) {
        console.error("Failed to select raw urgency:", error);
      }
    }}
    sx={{
      border: "none",
      background: "none",
      p: 0,
      cursor: "pointer",
      color: active ? activeColor : color,
      textDecoration: active ? "underline" : "none",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    <CustomTypography
      variant="caption"
      fontWeight={700}
      color={active ? activeColor : color}
    >
      {label}
    </CustomTypography>
  </Box>
);

export default RawWorkLedger;
